import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, Signal, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DataState, routes } from '@libs/utils';
import { Store } from '@ngrx/store';
import { NoteItem, ProgressNoteItem } from '@libs/notes';
import { NavigationService, pathParameter } from '@libs/shared-services';

import { patientActions, selectTeeth, selectTeethLoading } from '@libs/store';
import { ChartingScreenComponentService } from '../charting-screen/charting-screen.component.service';
import { chartingActions, selectScreen, selectViewToggle } from '../../store';
import { ToothDetailsComponent, ToothDetailsVM } from './tooth-details';
import { ToothDetailsDialogData } from './tooth-details-dialog.vm';

const TOP_LEFT = 1;
const TOP_RIGHT = 16;
const BOTTOM_RIGHT = 17;
const BOTTOM_LEFT = 32;

@Component({
  selector: 'ffd-tooth-details-dialog',
  templateUrl: './tooth-details-dialog.component.html',
  styleUrls: ['./tooth-details-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ToothDetailsComponent],
})
export class ToothDetailsDialogComponent {
  private readonly data: ToothDetailsDialogData = inject(DIALOG_DATA);
  private readonly store = inject(Store);

  private readonly router = inject(Router);
  private readonly navigationService = inject(NavigationService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dialogRef = inject(DialogRef);

  private readonly screenService = inject(ChartingScreenComponentService);
  private readonly teethWithLayers = computed(() => this.screenService.viewModel().teethWithLayers);
  private readonly images = computed(() => this.screenService.viewModel().toothDetailImages);
  private readonly visits = computed(() => this.screenService.viewModel().visits);
  private readonly visitsLoading = computed(() => this.screenService.viewModel().visitsLoading);
  private readonly hasImagingSoftware = computed(() => this.screenService.viewModel().hasImagingSoftware);
  private readonly isTimeZoneDifferentFromLocal = computed(() => this.screenService.viewModel().isTimeZoneDifferentFromLocal);
  private readonly baseRoute = [
    '/',
    routes.site,
    this.data.siteId,
    routes.patients,
    this.data.patientId,
    this.data.screen === 'charting' ? routes.charting : routes.perio,
  ];

  private readonly toothPosition = signal(this.data.toothPosition);

  protected readonly toothDetails: Signal<ToothDetailsVM> = computed(() => {
    const teethDataLoading = this.store.selectSignal(selectTeethLoading)();
    const teethData = this.store.selectSignal(selectTeeth)();

    return {
      siteId: this.data.siteId || '',
      patientId: this.data.patientId || '',
      isTimeZoneDifferentFromLocal: this.isTimeZoneDifferentFromLocal(),
      toothHistory: this.teethWithLayers()?.find((tooth) => tooth.position === this.toothPosition())?.historyStates,
      images: {
        data: this.images()
          .data?.map((image) => ({
            ...image,
            images: image.images.filter((i) => i.toothPositions?.includes(this.toothPosition())),
          }))
          .filter((image) => image.images.length > 0),
        state: this.images().state,
      },
      visits: this.visits(),
      visitsLoading: this.visitsLoading(),
      hasImagingSoftware: this.hasImagingSoftware(),
      screen: this.store.selectSignal(selectScreen)(),
      view: this.store.selectSignal(selectViewToggle)(),
      toothPosition: this.toothPosition(),
      perioExams:
        teethData?.perioExamsWithTeeth.map((exam) => ({
          date: exam.date,
          teeth: exam.teeth,
        })) ?? [],
      teethDataLoading: teethDataLoading,
      date: this.data.date,
    };
  });

  constructor() {
    effect(() => {
      if (this.hasImagingSoftware().data && this.hasImagingSoftware().state === DataState.Fetched) {
        this.store.dispatch(patientActions.onLoadImages({ siteId: this.data.siteId ?? '', patientId: this.data.patientId ?? '' }));
      }
    });

    effect(() => {
      this.store.dispatch(chartingActions.screenChanged({ screen: this.data.screen }));
    });

    effect(() => {
      this.store.dispatch(chartingActions.viewToggleChanged({ viewToggle: this.data.screen }));
    });

    effect(() => {
      const currentToothPosition = Number.parseInt(this.activatedRoute.snapshot.params[pathParameter('toothPosition')]);
      this.store.dispatch(chartingActions.lastToothPositionChanged({ toothPosition: this.toothPosition() }));
      if (currentToothPosition !== this.toothPosition()) {
        this.router.navigate([...this.baseRoute, routes.toothDetails, this.toothPosition()], { queryParamsHandling: 'preserve' });
      }
    });
  }

  protected onCloseClick(): void {
    this.router.navigate(this.baseRoute);
    this.store.dispatch(chartingActions.lastToothPositionChanged({ toothPosition: undefined }));
  }

  protected onToggleClick(id: 'charting' | 'perio'): void {
    this.store.dispatch(chartingActions.viewToggleChanged({ viewToggle: id }));
  }

  protected onLeftClick(): void {
    if (this.toothPosition() > TOP_LEFT && this.toothPosition() <= TOP_RIGHT) {
      this.toothPosition.set(this.toothPosition() - 1);
    } else if (this.toothPosition() < BOTTOM_LEFT && this.toothPosition() >= BOTTOM_RIGHT) {
      this.toothPosition.set(this.toothPosition() + 1);
    } else if (this.toothPosition() === TOP_LEFT) {
      this.toothPosition.set(BOTTOM_LEFT);
    } else if (this.toothPosition() === BOTTOM_LEFT) {
      this.toothPosition.set(TOP_LEFT);
    }
  }

  protected onRightClick(): void {
    if (this.toothPosition() >= TOP_LEFT && this.toothPosition() < TOP_RIGHT) {
      this.toothPosition.set(this.toothPosition() + 1);
    } else if (this.toothPosition() <= BOTTOM_LEFT && this.toothPosition() > BOTTOM_RIGHT) {
      this.toothPosition.set(this.toothPosition() - 1);
    } else if (this.toothPosition() === TOP_RIGHT) {
      this.toothPosition.set(BOTTOM_RIGHT);
    } else if (this.toothPosition() === BOTTOM_RIGHT) {
      this.toothPosition.set(TOP_RIGHT);
    }
  }

  public closeDialog(): void {
    this.onCloseClick();
    this.dialogRef.close();
  }

  protected onVisitDateClick(visitId: string): void {
    this.navigationService.navigate('notesOnSiteForPatient', false, [this.data.siteId!, this.data.patientId!], { visitId });
  }

  protected onHideConditionClick(noteId: number): void {
    this.screenService.hideCondition(noteId);
  }

  protected onDeleteProgressNoteClick(noteId: number): void {
    this.screenService.deleteProgressNote(noteId);
  }

  private isProgressNote(note: NoteItem): note is ProgressNoteItem {
    return 'procedureCode' in note;
  }
}
